/**
 * Text scaling components for dynamic text size adjustment
 */

import React from 'react';
import { View, StyleSheet, Pressable } from 'react-native';
import { Text, TextProps } from '../Text';
import { useTheme } from '../../themes';
import { useDynamicTextScale } from '../../hooks/useTypography';

export interface TextScaleControlsProps {
  onScaleChange?: (scale: string) => void;
  showLabels?: boolean;
}

/**
 * Text scale controls for accessibility
 */
export function TextScaleControls({
  onScaleChange,
  showLabels = true,
}: TextScaleControlsProps) {
  const { theme } = useTheme();
  const {
    textScale,
    scaleUp,
    scaleDown,
    resetScale,
    canScaleUp,
    canScaleDown,
  } = useDynamicTextScale();

  const handleScaleUp = () => {
    scaleUp();
    onScaleChange?.(textScale);
  };

  const handleScaleDown = () => {
    scaleDown();
    onScaleChange?.(textScale);
  };

  const handleReset = () => {
    resetScale();
    onScaleChange?.('base');
  };

  return (
    <View style={styles.container}>
      {showLabels && (
        <Text variant='label' style={styles.label}>
          Text Size: {textScale.toUpperCase()}
        </Text>
      )}

      <View style={styles.controls}>
        <Pressable
          style={[
            styles.button,
            {
              backgroundColor: theme.colors.surface.secondary,
              borderColor: theme.colors.border.primary,
              opacity: canScaleDown ? 1 : 0.5,
            },
          ]}
          onPress={handleScaleDown}
          disabled={!canScaleDown}
          accessibilityRole='button'
          accessibilityLabel='Decrease text size'
        >
          <Text variant='button' style={styles.buttonText}>
            A-
          </Text>
        </Pressable>

        <Pressable
          style={[
            styles.button,
            {
              backgroundColor: theme.colors.surface.secondary,
              borderColor: theme.colors.border.primary,
            },
          ]}
          onPress={handleReset}
          accessibilityRole='button'
          accessibilityLabel='Reset text size to default'
        >
          <Text variant='button' style={styles.buttonText}>
            Reset
          </Text>
        </Pressable>

        <Pressable
          style={[
            styles.button,
            {
              backgroundColor: theme.colors.surface.secondary,
              borderColor: theme.colors.border.primary,
              opacity: canScaleUp ? 1 : 0.5,
            },
          ]}
          onPress={handleScaleUp}
          disabled={!canScaleUp}
          accessibilityRole='button'
          accessibilityLabel='Increase text size'
        >
          <Text variant='button' style={styles.buttonText}>
            A+
          </Text>
        </Pressable>
      </View>
    </View>
  );
}

/**
 * Text scale preview component
 */
export function TextScalePreview() {
  const { textScale, getScaledText } = useDynamicTextScale();

  return (
    <View style={styles.preview}>
      <Text variant='h3' style={styles.previewTitle}>
        Text Scale Preview
      </Text>

      <View style={styles.previewContent}>
        <Text variant='body' style={styles.previewText}>
          This is how body text appears at {textScale} scale. The quick brown
          fox jumps over the lazy dog.
        </Text>

        <Text variant='caption' style={styles.previewCaption}>
          Caption text at {textScale} scale
        </Text>

        <Text variant='h4' style={styles.previewHeading}>
          Heading at {textScale} scale
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingVertical: 16,
  },
  label: {
    marginBottom: 12,
    textAlign: 'center',
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  button: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    minWidth: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  preview: {
    padding: 16,
    marginTop: 16,
  },
  previewTitle: {
    marginBottom: 16,
    textAlign: 'center',
  },
  previewContent: {
    gap: 12,
  },
  previewText: {
    // Text will scale automatically
  },
  previewCaption: {
    // Text will scale automatically
  },
  previewHeading: {
    // Text will scale automatically
  },
});
